<template>
  <WithAsideCards
    :data="data?.fields?.references?.filter?.(instanceOfAsideCard)"
    data-event-listing-v="3.4.8"
  >
    <template #header>
      <BaseHeading :class="$style.header">
        {{ data?.fields?.header }}
      </BaseHeading>
    </template>

    <div :class="$style.contentContainer">
      <div v-if="data?.fields?.subheader" :class="$style.headerContainer">
        <em :class="$style.subheader">{{ data?.fields?.subheader }}</em>
      </div>

      <MarkedParser :class="$style.copy" :md="data?.fields?.copy" />

      <div
        v-if="showFilters && isMounted"
        :class="$style.filters"
        @pointerdown.capture="markUserInteraction"
        @keydown.capture="markUserInteraction"
      >
        <BaseSelect
          v-model="selectedBrandFilter"
          :active-filter-value="selectedBrandFilter"
          :class="$style.select"
          :placeholder="localizedStrings['Brand'] || 'Brand'"
          :options="computedBrands"
          :clearable="true"
          :hide-text-overflow="true"
          @on-change="syncRouteQuery('brand:on-change')"
          @on-clear="syncRouteQuery('brand:on-clear')"
        />
        <BaseSelect
          v-model="selectedProductFilter"
          :active-filter-value="selectedProductFilter"
          :class="$style.select"
          :placeholder="localizedStrings['Product'] || 'Product'"
          :options="computedProducts"
          :clearable="true"
          :hide-text-overflow="true"
          @on-change="syncRouteQuery('product:on-change')"
          @on-clear="syncRouteQuery('product:on-clear')"
        />
        <SearchField
          :initial-query="searchQuery || undefined"
          :placeholder="'Search events...'"
          :class="$style.search"
          @request-query="handleSearch"
          @clear-query="handleSearch"
        />
      </div>

      <LoaderDots
        v-if="
          idsStatus === 'pending' ||
          (!pagedEvents.length && eventsStatus === 'pending')
        "
        :class="$style.loading"
      />

      <div
        v-else-if="
          idsStatus === 'success' &&
          eventsStatus === 'success' &&
          pagedEvents.length > 0
        "
        :class="$style.cards"
      >
        <EventCard
          v-for="event in pagedEvents"
          :key="event.sys.id"
          :data="event"
          :class="$style.card"
        />
      </div>

      <div
        v-else-if="
          idsStatus === 'success' &&
          eventsStatus === 'success' &&
          computedEvents.length === 0
        "
        :class="$style.noResults"
      >
        {{ localizedStrings['no-results'] || 'No Results' }}
      </div>

      <CtaWings
        v-if="
          idsStatus === 'success' &&
          eventsStatus === 'success' &&
          computedEvents.length < allEvents.length
        "
      >
        <CallToAction
          :cta="data?.fields?.ctas?.filter(instanceOfCallToAction)?.[0]"
          @click="onRequestMore"
        />
      </CtaWings>
    </div>
  </WithAsideCards>
  <!-- <DataPeek :data="pagedEvents" title="All Accessible Events" /> -->
</template>

<script setup lang="ts">
import type { TypeContentBlockWithoutUnresolvableLinksResponse } from '~/types/contentful'

import {
  instanceOfCallToAction,
  instanceOfAsideCard,
} from '~/utils/instanceOfEntry'

import BaseSelect from './BaseSelect.vue'
import { useEvents } from '~/composables/useEvents'

const { localizedStrings } = useLocalizedStrings()
const route = useRoute()
const router = useRouter()

const PAGE_SIZE = 6

const {
  data = undefined,
  // query = '',
  showFilters = true,
} = defineProps<{
  data?: TypeContentBlockWithoutUnresolvableLinksResponse
  // query?: string
  showFilters?: boolean
}>()

// Filters
const selectedBrandFilter = ref('')
const selectedProductFilter = ref('')
const isHydratingFromRoute = ref(false)
const hasInitialClientQuery = ref(false)
const hasUserInteracted = ref(false)
const isMounted = ref(false)
const isEventListingDebug = computed(() => {
  if (!import.meta.client) return false
  const debug = arrayAsString(route.query.debugEvents)
  if (debug === '1' || debug === 'true') return true
  return window.location.hostname.includes('stage.wpn.wizards.com')
})

const logEventListing = (step: string, payload?: unknown) => {
  if (!isEventListingDebug.value) return
  console.info('[EventListing]', step, payload ?? '')
}

const markUserInteraction = () => {
  hasUserInteracted.value = true
}

// Search
const searchQuery = defineModel<string | null>('query', { default: '' })

// Hook up the composable BEFORE hydrating from URL.
// This ensures the watch([q, b, p, locale]) inside useEvents is created
// while refs are still empty. When we set them from the URL below,
// the watch detects the change and triggers refresh().
const {
  // statuses
  idsStatus, // accessible IDs fetch status
  eventsStatus, // events fetch status

  // data
  allEvents, // full list (already sorted by urgency)
  pagedEvents, // paginated slice (based on pageSize & currentPage)
  brands,
  products,

  // controls
  currentPage,
  loadMore,
  refresh: refreshEvents,
} = useEvents({
  searchQuery: searchQuery,
  brandId: selectedBrandFilter,
  productId: selectedProductFilter,
  pageSize: PAGE_SIZE,
})

// URL hydration and filter rendering are deferred to onMounted.
// This prevents BaseSelect's onMounted from writing stale SSR-cached
// values into the parent refs before we've read the actual URL params.

// UI adapters
const computedBrands = computed(() =>
  [...(brands.value || [])].sort((a, b) => a.label.localeCompare(b.label)),
)

const computedProducts = computed(() =>
  [...(products.value || [])].sort((a, b) => a.label.localeCompare(b.label)),
)

const computedEvents = computed(() => {
  return pagedEvents.value || []
})

watch([selectedBrandFilter, selectedProductFilter, searchQuery], () => {
  // reset pagination when filters/search change
  logEventListing('state:filters-changed-reset-page', {
    q: searchQuery.value,
    brand: selectedBrandFilter.value,
    product: selectedProductFilter.value,
  })
  currentPage.value = 1
})

const handleSearch = (val: string) => {
  markUserInteraction()
  logEventListing('search:request', { next: val })
  searchQuery.value = val
  syncRouteQuery('search')
}
const onRequestMore = () => loadMore()

onMounted(() => {
  if (import.meta.client && showFilters) {
    // Read URL params BEFORE rendering filters (isMounted gates the v-if).
    // This ensures BaseSelect mounts with the correct values already set.
    const query = new URLSearchParams(window.location.search)
    searchQuery.value = query.get('q') || ''
    selectedBrandFilter.value = query.get('brand') || ''
    selectedProductFilter.value = query.get('product') || ''
    hasInitialClientQuery.value = !!(
      searchQuery.value ||
      selectedBrandFilter.value ||
      selectedProductFilter.value
    )

    logEventListing('client:onMounted', {
      q: searchQuery.value,
      brand: selectedBrandFilter.value,
      product: selectedProductFilter.value,
      hasInitialClientQuery: hasInitialClientQuery.value,
    })

    if (hasInitialClientQuery.value) {
      logEventListing('client:onMounted-refresh', {
        reason: 'initial-query-present',
      })
      refreshEvents()
    }

    // Now render the filters — BaseSelect will mount with correct values
    isMounted.value = true
  }
})

const syncRouteQuery = (source = 'unknown') => {
  if (!showFilters || isHydratingFromRoute.value) {
    logEventListing('route:skip-sync', {
      source,
      showFilters,
      isHydratingFromRoute: isHydratingFromRoute.value,
    })
    return
  }

  // CRITICAL: Prevent automatic redirects during initialization or hydration.
  // We only sync to the URL if the user has explicitly interacted with the filters,
  // OR if this is a search request (which is already guarded by markUserInteraction).
  if (!hasUserInteracted.value) {
    logEventListing('route:skip-sync-no-user-interaction', { source })
    return
  }

  const q = searchQuery.value || ''
  const brand = selectedBrandFilter.value || ''
  const product = selectedProductFilter.value || ''

  const currentQ = arrayAsString(route.query.q) || ''
  const currentBrand = arrayAsString(route.query.brand) || ''
  const currentProduct = arrayAsString(route.query.product) || ''

  if (q === currentQ && brand === currentBrand && product === currentProduct) {
    logEventListing('route:skip-sync-no-change', {
      source,
      next: { q, brand, product },
      current: { q: currentQ, brand: currentBrand, product: currentProduct },
    })
    return
  }

  const nextQuery = {
    ...route.query,
    q: q || undefined,
    brand: brand || undefined,
    product: product || undefined,
  }
  logEventListing('route:replace', {
    source,
    next: { q, brand, product },
    nextQuery,
  })
  void router
    .replace({
      query: nextQuery,
    })
    .then(() => {
      logEventListing('route:replace:done', { source, nextQuery })
    })
    .catch((error) => {
      logEventListing('route:replace:error', {
        source,
        message: String(error),
      })
    })
}

watch(
  () => [route.query.q, route.query.brand, route.query.product],
  ([q, brand, product]) => {
    if (!showFilters) return

    logEventListing('route:watch:incoming', {
      q: arrayAsString(q) || '',
      brand: arrayAsString(brand) || '',
      product: arrayAsString(product) || '',
    })
    isHydratingFromRoute.value = true
    searchQuery.value = arrayAsString(q) || ''
    selectedBrandFilter.value = arrayAsString(brand) || ''
    selectedProductFilter.value = arrayAsString(product) || ''
    nextTick(() => {
      isHydratingFromRoute.value = false
      logEventListing('route:watch:applied', {
        q: searchQuery.value,
        brand: selectedBrandFilter.value,
        product: selectedProductFilter.value,
      })
    })
  },
)
</script>

<style lang="scss" module>
.contentContainer {
  color: var(--gray-8);
  text-align: center;

  @include media('sm') {
    text-align: left;
  }
}

.headerContainer {
  max-width: 544px;
  margin: 0 auto 22px;

  @include media('sm') {
    margin-left: 0;
  }
}

.header {
  color: var(--black);
  @include fluid-type(32px, 52px);
}

.subheader {
  font-family: var(--georgia);
  line-height: math.div(32, 22);
  @include fluid-type(16px, 22px);
}

.copy {
  line-height: math.div(30, 19);
  margin-bottom: 20px;
  @include fluid-type(16px, 19px);
}

.filters {
  display: flex;
  margin-block: 20px;
  gap: 10px;
  flex-wrap: wrap;
}

.select,
.search {
  width: 100%;

  @include media('sm') {
    max-width: 250px;
  }
}

.search {
  @include media('sm') {
    max-width: 250px;
    min-width: 140px;
    flex: 1;
  }
}

.cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 17px;
}

.card {
  --padding--base: 0;
  --padding--lg: 0;
}

.cta {
  margin-top: 40px;
}

.cardContainer {
  display: grid;
  gap: 19px;
}

.loading {
  width: 100%;
  padding: 2em;
  text-align: center;
}
</style>
